import React from "react";
import "./desktop-menu.css";
import Logo from "../Logo";
import useMenu from "../../utils/menu";
import IconifyIcon from "../IconifyIcon";
import { Link, useLocation } from "react-router-dom";
import {
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectLabels,
  selectLanguage,
  setLanguage,
} from "../../slices/language";
import { Context } from "../../context/ContextProvider/ContextProvider";
import LanguageModal from "../Modals/LanguageModal";
import { selectUser } from "../../slices/user";
import { getPropertyLogoUrl } from "../../utils/getPropertyLogoUrl";

// interface DesktopMenuProps {
//   showMenu: boolean;
//   setShowMenu: (showMenu: boolean) => void;
// }

const DesktopMenu = () => {
  const labels = useAppSelector(selectLabels);
  const location = useLocation();
  const currentLanguage = useAppSelector(selectLanguage);
  const { fixedMenu, toggleSubMenu, openSubmenu } = useMenu();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const property = user?.property;
  const propertyImage = getPropertyLogoUrl(property);

  const { showDesktopMenu, setShowDesktopMenu } = React.useContext(Context);

  const [selectedLanguage, setSelectedLanguage] = React.useState<string>(
    currentLanguage.includes("es") ? "es" : "en"
  );
  const [showLanguageModal, setShowLanguageModal] =
    React.useState<boolean>(false);

  const languajes = [
    {
      label: labels.spanish,
      value: "es",
    },
    {
      label: labels.english,
      value: "en",
    },
  ];

  const handleLanguageChange = (event: SelectChangeEvent) => {
    const newLanguage = event.target.value as string;
    setSelectedLanguage(newLanguage);
    dispatch(setLanguage(newLanguage));
  };

  const handleLanguageChangeHideMenu = (value: string) => {
    setSelectedLanguage(value);
    dispatch(setLanguage(value));
  };

  return (
    <nav
      className={`left-menu p-8 border-box vh-100 scroll ${
        showDesktopMenu ? "" : "collapsed"
      }`}
      style={{ position: "fixed" }}
    >
      <LanguageModal
        show={showLanguageModal}
        setShow={setShowLanguageModal}
        currentLanguage={currentLanguage}
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={setSelectedLanguage}
        onChangeFunction={handleLanguageChangeHideMenu}
      />
      <div className={`logo-container ${showDesktopMenu ? "" : "collapsed"}`}>
        <div style={{ position: "absolute", top: 4, right: 4 }}>
          {showDesktopMenu ? (
            <IconButton
              size="small"
              style={{ display: "flex" }}
              onClick={() => setShowDesktopMenu(false)}
            >
              <IconifyIcon
                icon={"ci:chevron-left-duo"}
                style={{ fontSize: 30 }}
              />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              style={{ display: "flex" }}
              onClick={() => setShowDesktopMenu(true)}
            >
              <IconifyIcon
                icon={"ci:chevron-right-duo"}
                style={{ fontSize: 30 }}
              />
            </IconButton>
          )}
        </div>
        {propertyImage ? (
          <img
            src={propertyImage}
            alt={property?.name ? `${property.name} logo` : "Property logo"}
            className="menu-logo"
          />
        ) : (
          <Logo type={"passTrackLogo"} alt="passtrack" className="menu-logo" />
        )}
      </div>
      <div style={{ marginTop: "120px", marginBottom: "60px" }}>
        {showDesktopMenu ? (
          <>
            {fixedMenu.map((item, index) => (
              <div key={`${index}-${item?.label}`}>
                {item.show &&
                  (item.children.length > 0 ? (
                    <>
                      <div
                        className={
                          openSubmenu.includes(item.id)
                            ? `plegable-menu-link justify-content-between pointer`
                            : `menu-link justify-content-between pointer`
                        }
                        onClick={() => toggleSubMenu(item.id)}
                      >
                        <div className="flex align-items-center">
                          <IconifyIcon icon={item?.icon} />
                          <p className="ml-4 select-none">{item?.label}</p>
                        </div>
                        {openSubmenu.includes(item.id) ? (
                          <IconifyIcon icon={"fa6-solid:angle-down"} />
                        ) : (
                          <IconifyIcon icon={"fa6-solid:angle-right"} />
                        )}
                      </div>
                      {openSubmenu.includes(item.id) && (
                        <>
                          {item.children.map((child, index) => (
                            <Link
                              key={`${index}-${child.label}`}
                              to={child?.route}
                              style={{ textDecoration: "none" }}
                            >
                              <div
                                className={`menu-link ${
                                  location.pathname === child?.route
                                    ? "selected"
                                    : ""
                                }`}
                                style={{ paddingLeft: 24 }}
                              >
                                {location.pathname === child?.route ? (
                                  <IconifyIcon
                                    icon={"fa6-regular:circle-dot"}
                                    style={{ fontSize: 14 }}
                                  />
                                ) : (
                                  <IconifyIcon
                                    icon={"fa6-regular:circle"}
                                    style={{ fontSize: 14 }}
                                  />
                                )}
                                <p
                                  className="ml-4 select-none"
                                  style={{
                                    fontWeight:
                                      location.pathname === child?.route
                                        ? "bold"
                                        : "",
                                  }}
                                >
                                  {child?.label}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <Link to={item?.route} style={{ textDecoration: "none" }}>
                      <div
                        className={`menu-link ${
                          location.pathname === item?.route ? "selected" : ""
                        }`}
                      >
                        <IconifyIcon icon={item?.icon} />
                        <p
                          className="ml-4 select-none"
                          style={{
                            fontWeight:
                              location.pathname === item?.route ? "bold" : "",
                          }}
                        >
                          {item?.label}
                        </p>
                      </div>
                    </Link>
                  ))}
              </div>
            ))}
          </>
        ) : (
          <>
            {fixedMenu.map((item, index) => (
              <div key={`${index}-${item?.label}`}>
                {item?.show &&
                  (item.children.length === 0 ? (
                    <Tooltip
                      disableInteractive
                      title={item?.label}
                      placement="left"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: "offset",
                              options: {
                                offset: [0, -10],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <Link to={item?.route} style={{ textDecoration: "none" }}>
                        <div
                          className={`menu-link ${
                            location.pathname === item?.route ? "selected" : ""
                          }`}
                          style={{ justifyContent: "center" }}
                        >
                          <IconifyIcon icon={item?.icon} />
                        </div>
                      </Link>
                    </Tooltip>
                  ) : (
                    <>
                      {item.children.map((child, index) => (
                        <Tooltip
                          disableInteractive
                          title={child?.label}
                          placement="left"
                          slotProps={{
                            popper: {
                              modifiers: [
                                {
                                  name: "offset",
                                  options: {
                                    offset: [0, -10],
                                  },
                                },
                              ],
                            },
                          }}
                        >
                          <Link
                            key={`${index}-${child.label}`}
                            to={child?.route}
                            style={{ textDecoration: "none" }}
                          >
                            <div
                              className={`menu-link ${
                                location.pathname === child?.route
                                  ? "selected"
                                  : ""
                              }`}
                              style={{ justifyContent: "center" }}
                            >
                              <IconifyIcon icon={child.icon} />
                            </div>
                          </Link>
                        </Tooltip>
                      ))}
                    </>
                  ))}
              </div>
            ))}
          </>
        )}
      </div>
      <div
        className={`footer-menu-container ${
          showDesktopMenu ? "" : "collapsed"
        }`}
        style={{ background: "white" }}
      >
        {showDesktopMenu ? (
          <div className="w-100 px-8 flex align-items-center">
            <IconifyIcon
              icon={"material-symbols:language"}
              style={{ fontSize: 20, marginRight: 8 }}
            />
            <FormControl
              fullWidth
              variant="standard"
              sx={{ minWidth: 120 }}
              size="small"
            >
              <Select value={selectedLanguage} onChange={handleLanguageChange}>
                {languajes.map((item, index) => (
                  <MenuItem key={`${index}-${item?.value}`} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        ) : (
          <div className="w-100 flex justify-content-center">
            <IconButton
              onClick={() => setShowLanguageModal(!showLanguageModal)}
            >
              <IconifyIcon
                icon={"material-symbols:language"}
                style={{ fontSize: 20 }}
              />
            </IconButton>
          </div>
        )}
      </div>
    </nav>
  );
};

export default DesktopMenu;
